import FingerprintJS from '@fingerprintjs/fingerprintjs'
import Environment from '@interfaces/environment.interface'
import onEnvironmentChange from '@observables/environment.observable'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import { AxiosError, AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'

export default class SheHulkService extends MainService {
  url: string
  GEOIP_URL: string
  logService = new LogService()

  constructor(shehulkUrl?: string) {
    super()
    if (!shehulkUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.sheHulk
        this.GEOIP_URL = environment?.exteralApis?.geoip
      })
    } else {
      this.url = shehulkUrl
    }
    this.logService.init('Shehulk Service')
  }

  async getIp() {
    try {
      const { data } = (await this.http.get(this.GEOIP_URL, { headers: { Accept: 'application/json' } })) || {}
      return data?.ip
    } catch (error) {
      this.logService.error({ message: 'Error on get IP', error })
      return false
    }
  }

  public async getPublicContract(type = 'plataform', id = 'hub', lang = 'br') {
    if (lang.includes('pt')) {
      lang = 'br'
    }
    const headers = await this.getHeaders(false)
    return this.http
      .get(`${this.url}/public/contract/${type}/${id}?lang=${lang}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get contract',
          error,
        })
        return error.response
      })
  }

  async checkUserAcceptedContract(type = 'plataform', id = 'hub') {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/hub/${type}/${id}/contract/accepted`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get contract',
          error,
        })
        return {
          acceptedCurrent: true,
          acceptedCustom: true,
          acceptedSome: true,
          error: true,
        }
      })
  }

  async postAcceptedContract(type = 'plataform', id = 'hub') {
    const headers = await this.getHeaders()
    return this.http
      .post(
        `${this.url}/hub/${type}/${id}/contract/accepted`,
        {
          ip: await this.getIp(),
          accepted: true,
        },
        { headers }
      )
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on accept contract',
          error,
        })
        return {
          acceptedCurrent: true,
          acceptedCustom: true,
          acceptedSome: true,
          error: true,
        }
      })
  }

  public async getAcceptanceContract(campaignId: string, profileId?: string) {
    const headers = await this.getHeaders()
    const fp = await FingerprintJS.load()
    const { visitorId } = await fp.get()
    const ip = await this.getIp()
    headers['X-Fingerprint'] = `${navigator.userAgent}-${visitorId}`
    headers['X-IP'] = ip
    const query = new URLSearchParams()
    if (profileId) {
      query.append('profileId', profileId)
    }
    return this.http
      .get(`${this.url}/hub/contract/${campaignId}${query.toString()?.length ? '?' + query.toString() : ''}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get contract',
          error,
        })
        throw error.response
      })
  }

  public async getAcceptedContract(campaignId: string, profileId: string) {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/hub/campaign-contract/${campaignId}?profileId=${profileId}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on get contract',
          error,
        })
        throw error.response
      })
  }

  public async getDownloadContract(campaignId: string): Promise<Blob> {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/hub/contract/download/campaign/${campaignId}`, {
        headers: { ...headers, Accept: 'application/pdf' },
        responseType: 'blob',
      })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get contract',
          error,
        })
        return error.response
      })
  }

  public async getAntecipationContract(transactionId: string) {
    const headers = await this.getHeaders()
    return this.http
      .get(`${this.url}/hub/contract/anticipate/${transactionId}`, { headers })
      .then((r: AxiosResponse) => r.data)
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on get antecipation contract',
          error,
        })
        return error.response
      })
  }
}
